import { FC, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import classes from './topCustomerIntents.module.scss'

export type SegmentKey = 'negative' | 'neutral' | 'positive'

export const SEGMENT_META: Record<
  SegmentKey,
  { color: string; label: string }
> = {
  negative: { color: '#FF6A33', label: 'Negative' },
  neutral: { color: '#C7CED1', label: 'Neutral' },
  positive: { color: '#8080FF', label: 'Positive' },
}

export interface IntentRow {
  id: string
  name: string
  negative: number
  neutral: number
  positive: number
  total: number
}

// Dot color = the dominant sentiment for the intent.
export const dominantColor = (row: IntentRow): string => {
  if (row.negative >= row.positive && row.negative >= row.neutral) {
    return SEGMENT_META.negative.color
  }
  return row.positive >= row.neutral
    ? SEGMENT_META.positive.color
    : SEGMENT_META.neutral.color
}

// ── Truncated label with portal tooltip ─────────────────────────

interface TruncatedLabelProps {
  className?: string
  text: string
}

export const TruncatedLabel: FC<TruncatedLabelProps> = ({
  className,
  text,
}) => {
  const ref = useRef<HTMLSpanElement>(null)
  const [tipPos, setTipPos] = useState<{ left: number; top: number } | null>(
    null
  )

  const handleEnter = () => {
    const el = ref.current
    if (!el || el.scrollWidth <= el.clientWidth) return
    const rect = el.getBoundingClientRect()
    setTipPos({ left: rect.left + rect.width / 2, top: rect.top - 8 })
  }

  return (
    <>
      <span
        className={className ?? classes.intentName}
        onMouseEnter={handleEnter}
        onMouseLeave={() => setTipPos(null)}
        ref={ref}
      >
        {text}
      </span>
      {tipPos &&
        createPortal(
          <div
            className={classes.intentTooltip}
            style={{ left: tipPos.left, top: tipPos.top }}
          >
            {text}
          </div>,
          document.body
        )}
    </>
  )
}

// ── Stacked sentiment bar + labels ───────────────────────────────

interface SentimentBarProps {
  row: IntentRow
}

export const SentimentBar: FC<SentimentBarProps> = ({ row }) => {
  const { negative, neutral, positive, total } = row
  if (total === 0) return null

  const pctOf = (n: number) => Math.round((n / total) * 100)
  const positivePct = pctOf(positive)
  const neutralPct = pctOf(neutral)
  const negativePct = 100 - positivePct - neutralPct

  const segments: { count: number; key: SegmentKey; pct: number }[] = (
    [
      { count: positive, key: 'positive' as const, pct: positivePct },
      { count: neutral, key: 'neutral' as const, pct: neutralPct },
      { count: negative, key: 'negative' as const, pct: negativePct },
    ] as { count: number; key: SegmentKey; pct: number }[]
  ).filter((s) => s.pct > 0)

  return (
    <div className={classes.sentimentBar}>
      <div className={classes.barTrack}>
        {segments.map((seg, i) => (
          <span
            className={classes.barSegment}
            key={seg.key}
            style={{
              background: SEGMENT_META[seg.key].color,
              borderRadius:
                i === 0 && i === segments.length - 1
                  ? '20px'
                  : i === 0
                    ? '20px 0 0 20px'
                    : i === segments.length - 1
                      ? '0 20px 20px 0'
                      : '0',
              width: `${seg.pct}%`,
            }}
          />
        ))}
      </div>

      <div className={classes.barLabels}>
        {segments.map((seg) => (
          <span
            className={`${classes.barLabel} ${classes[seg.key]}`}
            key={seg.key}
          >
            {seg.pct}% {SEGMENT_META[seg.key].label} · {seg.count} Messages
          </span>
        ))}
      </div>
    </div>
  )
}
